import {
    Location,
    push,
    PushAction,
    Query,
    replace,
    ReplaceAction
} from 'redux-little-router';

import { ApiMethodExecutingError } from '../service/Errors';
import { captureException } from '../service/ErrorsReporting/ErrorsReporting';
import { UrlHelpers } from '../service/UrlHelpers';
import { MetroStationInfo } from '../typings/banners';
import { AuthorizationData, CommonData, LinkTarget } from '../typings/common';
import { CryptaSegment } from '../typings/cryptaSegments';
import {
    IndoorMapCommonData,
    OutdoorMapCommonData
} from '../typings/displayMap';
import { StringDictsItems } from '../typings/groups';
import {
    SidebarDynamicItemsIds,
    SidebarStaticItemsIds
} from '../typings/sidebar';
import { Regions } from '../typings/state';
import { TimeZoneGroup } from '../typings/timeTargetingEditor';

import { ClientFreelancerDataAction } from './clientFreelancerData';
import { ClientMobileAppsDataAction } from './clientMobileAppsData';

export const FETCH_COMMON_DATA = 'FETCH_COMMON_DATA';
export type FETCH_COMMON_DATA = typeof FETCH_COMMON_DATA;

export interface FetchCommonData {
    type: FETCH_COMMON_DATA;
    login: string;
}

export function fetchCommonData(login: string): FetchCommonData {
    return {
        type: FETCH_COMMON_DATA,
        login
    };
}

export const UPDATE_COMMON_DATA = 'UPDATE_COMMON_DATA';
export type UPDATE_COMMON_DATA = typeof UPDATE_COMMON_DATA;

export interface UpdateCommonData {
    type: UPDATE_COMMON_DATA;
    commonData: CommonData;
}

export function updateCommonData(commonData: CommonData): UpdateCommonData {
    return {
        type: UPDATE_COMMON_DATA,
        commonData: commonData
    };
}

export const DUMMY_ACTION = 'DUMMY_ACTION';
export type DUMMY_ACTION = typeof DUMMY_ACTION;

export interface DummyAction {
    type: DUMMY_ACTION;
}
export const dummyAction: DummyAction = {
    type: DUMMY_ACTION
};

export function changeLocation(
    url: string,
    query?: Query,
    persistQuery: boolean = true
): PushAction {
    let resultQuery = query;
    let resultPath = url;

    // Если не задан query, то мы пробуем получить его из URL (для случая когда переход задан только урлом)
    if (!query) {
        const parsedUrl = UrlHelpers.getFromUrlPathAndQuery(url);
        resultQuery = parsedUrl.query;
        resultPath = parsedUrl.path;
    }

    return push({ pathname: resultPath, query: resultQuery }, { persistQuery });
}

export function replaceLocation(
    url: string,
    query?: Query,
    persistQuery: boolean = true
): ReplaceAction {
    let resultQuery = query;
    let resultPath = url;

    // Если не задан query, то мы пробуем получить его из URL (для случая когда переход задан только урлом)
    if (!query) {
        const parsedUrl = UrlHelpers.getFromUrlPathAndQuery(url);
        resultQuery = parsedUrl.query;
        resultPath = parsedUrl.path;
    }

    return replace(
        { pathname: resultPath, query: resultQuery },
        { persistQuery }
    );
}

export const CHANGE_LOCATION_HARD = 'CHANGE_LOCATION_HARD';
export type CHANGE_LOCATION_HARD = typeof CHANGE_LOCATION_HARD;

export interface ChangeLocationHard {
    type: CHANGE_LOCATION_HARD;
    url: string;
    target?: LinkTarget;
}

export function changeLocationHard(
    url: string,
    target?: LinkTarget
): ChangeLocationHard {
    return {
        type: CHANGE_LOCATION_HARD,
        url,
        target
    };
}

export const REDIRECT_TO_LOCATION = 'REDIRECT_TO_LOCATION';
export type REDIRECT_TO_LOCATION = typeof REDIRECT_TO_LOCATION;

export interface RedirectToLocationAction {
    type: REDIRECT_TO_LOCATION;
    location: Location;
}

export function redirect(location: Location): RedirectToLocationAction {
    return {
        type: REDIRECT_TO_LOCATION,
        location
    };
}

export const REQUEST_AUTHORIZATION_DATA = 'REQUEST_AUTHORIZATION_DATA';
export type REQUEST_AUTHORIZATION_DATA = typeof REQUEST_AUTHORIZATION_DATA;

export const UPDATE_AUTHORIZATION_DATA = 'UPDATE_AUTHORIZATION_DATA';
export type UPDATE_AUTHORIZATION_DATA = typeof UPDATE_AUTHORIZATION_DATA;

export const REJECT_REQUEST_AUTHORIZATION_DATA =
    'REJECT_REQUEST_AUTHORIZATION_DATA';
export type REJECT_REQUEST_AUTHORIZATION_DATA = typeof REJECT_REQUEST_AUTHORIZATION_DATA;

export interface RequestAuthorizationDataAction {
    type: REQUEST_AUTHORIZATION_DATA;
}

export interface UpdateAuthorizationDataAction {
    type: UPDATE_AUTHORIZATION_DATA;
    data?: AuthorizationData;
}

export interface RejectRequestAuthorizationDataAction {
    type: REJECT_REQUEST_AUTHORIZATION_DATA;
}

export function requestAuthorizationData(): RequestAuthorizationDataAction {
    return {
        type: REQUEST_AUTHORIZATION_DATA
    };
}

export function updateAuthorizationData(
    data?: AuthorizationData
): UpdateAuthorizationDataAction {
    return {
        type: UPDATE_AUTHORIZATION_DATA,
        data
    };
}

export function rejectRequestAuthorizationData(): RejectRequestAuthorizationDataAction {
    return {
        type: REJECT_REQUEST_AUTHORIZATION_DATA
    };
}

export const UPDATE_REGIONS = 'UPDATE_REGIONS';
export type UPDATE_REGIONS = typeof UPDATE_REGIONS;

export interface UpdateRegionsAction {
    type: UPDATE_REGIONS;
    regions: Regions;
}

export function updateRegions(regions: Regions): UpdateRegionsAction {
    return {
        type: UPDATE_REGIONS,
        regions
    };
}

export const UPDATE_STRING_DICTS = 'UPDATE_STRING_DICTS';
export type UPDATE_STRING_DICTS = typeof UPDATE_STRING_DICTS;

export interface UpdateStringDictsAction {
    type: UPDATE_STRING_DICTS;
    stringDicts: StringDictsItems;
}

export function updateStringDicts(
    stringDicts: StringDictsItems
): UpdateStringDictsAction {
    return {
        type: UPDATE_STRING_DICTS,
        stringDicts
    };
}

export const UPDATE_CRYPTA_SEGMENTS = 'UPDATE_CRYPTA_SEGMENTS';
export type UPDATE_CRYPTA_SEGMENTS = typeof UPDATE_CRYPTA_SEGMENTS;

export interface UpdateCryptaSegmentsAction {
    type: UPDATE_CRYPTA_SEGMENTS;
    cryptaSegments: CryptaSegment[];
}

export function updateCryptaSegments(
    cryptaSegments: CryptaSegment[]
): UpdateCryptaSegmentsAction {
    return {
        type: UPDATE_CRYPTA_SEGMENTS,
        cryptaSegments
    };
}

export const UPDATE_OUTDOOR_MAP_DATA = 'UPDATE_OUTDOOR_MAP_DATA';
export type UPDATE_OUTDOOR_MAP_DATA = typeof UPDATE_OUTDOOR_MAP_DATA;

export interface UpdateOutdoorMapDataAction {
    type: UPDATE_OUTDOOR_MAP_DATA;
    outdoorMapData: OutdoorMapCommonData;
}

export function updateOutdoorMapData(
    outdoorMapData: OutdoorMapCommonData
): UpdateOutdoorMapDataAction {
    return {
        type: UPDATE_OUTDOOR_MAP_DATA,
        outdoorMapData
    };
}

export const UPDATE_INDOOR_MAP_DATA = 'UPDATE_INDOOR_MAP_DATA';
export type UPDATE_INDOOR_MAP_DATA = typeof UPDATE_INDOOR_MAP_DATA;

export interface UpdateIndoorMapDataAction {
    type: UPDATE_INDOOR_MAP_DATA;
    indoorMapData: IndoorMapCommonData;
}

export function updateIndoorMapData(
    indoorMapData: IndoorMapCommonData
): UpdateIndoorMapDataAction {
    return {
        type: UPDATE_INDOOR_MAP_DATA,
        indoorMapData
    };
}

export const UPDATE_DOCUMENT_TITLE = 'UPDATE_DOCUMENT_TITLE';
export type UPDATE_DOCUMENT_TITLE = typeof UPDATE_DOCUMENT_TITLE;

export interface UpdateDocumentTitle {
    type: UPDATE_DOCUMENT_TITLE;
    title: string;
}

export function updateDocumentTitle(title: string): UpdateDocumentTitle {
    return {
        type: UPDATE_DOCUMENT_TITLE,
        title
    };
}

export const SET_COOKIE = 'SET_COOKIE';
export type SET_COOKIE = typeof SET_COOKIE;

export interface SetCookie {
    type: SET_COOKIE;
    key: string;
    value: string;
}

export function setCookie(key: string, value: string): SetCookie {
    return {
        type: SET_COOKIE,
        key,
        value
    };
}

export const DELETE_COOKIE = 'DELETE_COOKIE';
export type DELETE_COOKIE = typeof DELETE_COOKIE;

export interface DeleteCookie {
    type: DELETE_COOKIE;
    key: string;
}

export function deleteCookie(key: string): DeleteCookie {
    return {
        type: DELETE_COOKIE,
        key
    };
}

export const RELOAD_LOCATION = 'RELOAD_LOCATION';
export type RELOAD_LOCATION = typeof RELOAD_LOCATION;

export interface ReloadLocation {
    type: RELOAD_LOCATION;
}

export function reloadLocation(): ReloadLocation {
    return {
        type: RELOAD_LOCATION
    };
}

export const SET_USER_LOGIN = 'SET_USER_LOGIN';
export type SET_USER_LOGIN = typeof SET_USER_LOGIN;

export interface SetUserLoginAction {
    type: SET_USER_LOGIN;
    login: string;
}

export function setUserLogin(login: string): SetUserLoginAction {
    return {
        type: SET_USER_LOGIN,
        login
    };
}

export const TOGGLE_SIDEBAR_EXPAND = 'TOGGLE_SIDEBAR_EXPAND';
export type TOGGLE_SIDEBAR_EXPAND = typeof TOGGLE_SIDEBAR_EXPAND;

export interface ToggleSidebarExpand {
    type: TOGGLE_SIDEBAR_EXPAND;
}

export function toggleSidebarExpand(): ToggleSidebarExpand {
    return {
        type: TOGGLE_SIDEBAR_EXPAND
    };
}

export const PROCESS_SIDEBAR_CLICK = 'PROCESS_SIDEBAR_CLICK';
export type PROCESS_SIDEBAR_CLICK = typeof PROCESS_SIDEBAR_CLICK;

export interface ProcessSidebarClick {
    type: PROCESS_SIDEBAR_CLICK;
    id: SidebarStaticItemsIds | SidebarDynamicItemsIds;
}

export function processSidebarClick(
    id: SidebarStaticItemsIds | SidebarDynamicItemsIds
): ProcessSidebarClick {
    return {
        type: PROCESS_SIDEBAR_CLICK,
        id
    };
}

export const CLOSE_SIDEBAR_MENUS = 'CLOSE_SIDEBAR_MENUS';
export type CLOSE_SIDEBAR_MENUS = typeof CLOSE_SIDEBAR_MENUS;

export interface CloseSidebarMenus {
    type: CLOSE_SIDEBAR_MENUS;
}

export function closeSidebarMenus(): CloseSidebarMenus {
    return {
        type: CLOSE_SIDEBAR_MENUS
    };
}

export const SHOW_COMMON_DATA_FETCH_ERROR = 'SHOW_COMMON_DATA_FETCH_ERROR';
export type SHOW_COMMON_DATA_FETCH_ERROR = typeof SHOW_COMMON_DATA_FETCH_ERROR;

export interface ShowCommonDataFetchError {
    type: SHOW_COMMON_DATA_FETCH_ERROR;
    message: string;
    title: string;
    showLinkSwitchOnOldInterface?: boolean;
}

export function showCommonDataFetchError(
    error: ApiMethodExecutingError | string,
    showLink?: boolean
): ShowCommonDataFetchError {
    captureException(
        error instanceof ApiMethodExecutingError
            ? error.getNativeError()
            : new Error(error)
    );

    return {
        type: SHOW_COMMON_DATA_FETCH_ERROR,
        message:
            error instanceof ApiMethodExecutingError
                ? error.getMessage()
                : error,
        title: error instanceof ApiMethodExecutingError ? error.getTitle() : '',
        showLinkSwitchOnOldInterface: showLink
    };
}

export const HIDE_RECOMMENDATIONS_TOOLTIP = 'HIDE_RECOMMENDATIONS_TOOLTIP';
export type HIDE_RECOMMENDATIONS_TOOLTIP = typeof HIDE_RECOMMENDATIONS_TOOLTIP;
export interface HideRecommendationsTooltip {
    type: HIDE_RECOMMENDATIONS_TOOLTIP;
}
export function hideRecommendationsTooltip(): HideRecommendationsTooltip {
    return {
        type: HIDE_RECOMMENDATIONS_TOOLTIP
    };
}

export const UPDATE_METRO_STATIONS = 'UPDATE_METRO_STATIONS';
export type UPDATE_METRO_STATIONS = typeof UPDATE_METRO_STATIONS;

export interface UpdateMetroStations {
    type: UPDATE_METRO_STATIONS;
    stations: Record<number, Array<MetroStationInfo>>;
}

export function updateMetroStations(
    stations: Record<number, Array<MetroStationInfo>>
): UpdateMetroStations {
    return {
        type: UPDATE_METRO_STATIONS,
        stations
    };
}

export const UPDATE_TIME_ZONE_GROUPS = 'UPDATE_TIME_ZONE_GROUPS';
export type UPDATE_TIME_ZONE_GROUPS = typeof UPDATE_TIME_ZONE_GROUPS;

export interface UpdateTimeZoneGroups {
    type: UPDATE_TIME_ZONE_GROUPS;
    timeZoneGroups: TimeZoneGroup[];
}
export function updateTimeZoneGroups(
    timeZoneGroups: TimeZoneGroup[]
): UpdateTimeZoneGroups {
    return {
        type: UPDATE_TIME_ZONE_GROUPS,
        timeZoneGroups
    };
}

export type CommonAction =
    | FetchCommonData
    | UpdateCommonData
    | DummyAction
    | ReloadLocation
    | UpdateDocumentTitle
    | ShowCommonDataFetchError
    | RequestAuthorizationDataAction
    | UpdateAuthorizationDataAction
    | RejectRequestAuthorizationDataAction
    | ClientFreelancerDataAction
    | ClientMobileAppsDataAction
    | ChangeLocationHard
    | RedirectToLocationAction
    | UpdateRegionsAction
    | UpdateStringDictsAction
    | UpdateCryptaSegmentsAction
    | UpdateOutdoorMapDataAction
    | UpdateIndoorMapDataAction
    | SetCookie
    | SetUserLoginAction
    | DeleteCookie
    | ToggleSidebarExpand
    | ProcessSidebarClick
    | CloseSidebarMenus
    | HideRecommendationsTooltip
    | UpdateMetroStations
    | UpdateTimeZoneGroups;
